import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import LeadForm from "@/components/LeadForm";
import Marquee from "@/components/Marquee";
import { KineticLines, Reveal } from "@/components/Reveal";
import { IMG } from "@/lib/images";
import { useSeo, JsonLd } from "@/lib/seo";
import { track } from "@/lib/track";

const DISCLAIMER =
  "*All figures are indicative planning estimates and are not quotations or guarantees. Actual investment may vary depending on store size, location, site condition, civil/electrical requirements, equipment selection, local statutory requirements, taxes and other project-specific factors. Final commercial terms will be confirmed following site evaluation and execution of the applicable franchise agreement. The Founding 10 franchise-fee waiver applies only to the first 10 franchise partners formally approved by RIFIC under the programme and is subject to programme terms.";

const INVESTMENT_ITEMS = [
  ["Store Interiors", "₹3,00,000", "Civil/fit-out work, electrical work, furniture, signage and basic store finishes."],
  ["Machines & Equipment", "₹3,00,000", "Ice cream display/freezer equipment, refrigeration, service equipment, POS and essential operating equipment."],
  ["Franchise Fee", "₹1,00,000", "One-time standard RIFIC franchise fee. ₹0 for the Founding 10."],
  ["Launch Marketing", "₹50,000", "Indicative initial store-launch marketing allocation."],
  ["Licences & Approvals", "₹25,000", "Indicative provision for applicable local licences and approvals."],
  ["Training Fee", "₹0", "No separate training fee."],
];

const COMPARE = [
  {
    title: "Standard RIFIC Parlour",
    tid: "investment-standard",
    rows: [
      ["Store Interiors", "₹3.00L"],
      ["Machines & Equipment", "₹3.00L"],
      ["Franchise Fee", "₹1.00L"],
      ["Launch Marketing", "₹0.50L"],
      ["Licences", "₹0.25L"],
    ],
    total: "₹7.75 LAKH",
    dark: false,
  },
  {
    title: "Founding 10 Partner",
    tid: "investment-founding10",
    rows: [
      ["Store Interiors", "₹3.00L"],
      ["Machines & Equipment", "₹3.00L"],
      ["Franchise Fee", "₹0"],
      ["Launch Marketing", "₹0.50L"],
      ["Licences", "₹0.25L"],
    ],
    total: "₹6.75 LAKH*",
    dark: true,
  },
];

const FAQS = [
  {
    q: "What is the RIFIC franchise fee?",
    a: "The standard franchise fee for the RIFIC Parlour model is ₹1 lakh. For the first 10 approved franchise partners, this fee is completely waived under the Founding 10 programme.",
  },
  {
    q: "How much investment is required?",
    a: "The RIFIC Parlour model is designed for approximately 200–300 sq. ft. The current indicative project investment is up to ₹8 lakh, depending on location, site condition and project-specific requirements.",
  },
  {
    q: "What does the indicative investment include?",
    a: "The current indicative structure provides approximately ₹3 lakh for store interiors, ₹3 lakh for machines and equipment, ₹1 lakh standard franchise fee, ₹50,000 for launch marketing and ₹25,000 for licences and approvals. There is no separate training fee.",
  },
  {
    q: "What is the Founding 10 offer?",
    a: "RIFIC will waive the standard ₹1 lakh franchise fee for the first 10 franchise partners formally approved under the Founding 10 programme.",
  },
  {
    q: "Does submitting an application guarantee the ₹0 fee?",
    a: "No. Submission does not guarantee selection. The offer applies only to the first 10 franchise partners formally approved by RIFIC under the programme.",
  },
  {
    q: "Is there a training fee?",
    a: "No. RIFIC does not currently charge a separate training fee for this franchise model.",
  },
];

const Section = ({ no, title, children, dark = false }) => (
  <section className={`py-12 md:py-16 ${dark ? "bg-deep text-cream" : ""}`}>
    <div className="mx-auto max-w-[1440px] px-5 md:px-10">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-orange-rific md:text-xs">{no}</p>
        <h2 className="mt-3 font-display text-[clamp(2rem,4.2vw,3.25rem)] font-extrabold leading-[1.05] tracking-[-0.01em]">{title}</h2>
      </Reveal>
      <Reveal delay={0.1}>{children}</Reveal>
    </div>
  </section>
);

const ApplyCta = ({ placement, className = "" }) => (
  <a
    href="#franchise-form"
    data-testid={`founding10-apply-${placement}`}
    onClick={() => track("franchise_cta_click", { placement, campaign: "founding_10" })}
    className={`inline-flex h-14 items-center gap-2 rounded-full bg-orange-rific px-9 text-sm font-bold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-deep ${className}`}
  >
    Apply for the Founding 10
    <ArrowDown className="h-4 w-4" />
  </a>
);

export default function Franchise() {
  useSeo({
    title: "RIFIC Franchise | RIFIC Parlour — Up to ₹8 Lakh Investment",
    description:
      "Own a RIFIC Parlour franchise — a 200–300 sq. ft. real fruit ice cream parlour with an indicative investment of up to ₹8 lakh. Founding 10 partners pay ₹0 franchise fee.",
    path: "/franchise",
  });

  return (
    <div data-testid="franchise-page">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />

      {/* HERO — TEXT | IMAGE */}
      <section className="relative overflow-hidden bg-deep" data-testid="franchise-hero">
        <div className="mx-auto grid max-w-[1440px] items-center gap-10 px-5 pb-14 pt-28 md:px-10 md:pb-20 md:pt-40 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-orange-rific md:text-xs">Franchise</p>
            </Reveal>
            <KineticLines
              mode="load"
              lines={["OWN A RIFIC."]}
              className="mt-4 block"
              lineClassName="font-display text-[clamp(2.875rem,6vw,5.5rem)] font-extrabold leading-[0.98] tracking-[-0.01em] text-cream"
            />
            <KineticLines
              mode="load"
              delay={0.3}
              lines={["BRING REAL TASTE TO YOUR CITY."]}
              className="mt-3 block"
              lineClassName="font-display text-[clamp(1.375rem,2.6vw,2rem)] font-bold leading-[1.1] tracking-[-0.01em] text-orange-rific"
            />

            <Reveal delay={0.4}>
              <div className="mt-8 grid grid-cols-3 gap-px overflow-hidden rounded-2xl bg-deep-line" data-testid="franchise-hero-model">
                {[
                  ["RIFIC PARLOUR", "Model"],
                  ["200–300 SQ. FT.", "Store Size"],
                  ["UP TO ₹8 LAKH*", "Indicative Investment"],
                ].map(([v, l]) => (
                  <div key={l} className="bg-deep px-3 py-4 text-center">
                    <p className="font-display text-sm font-bold text-cream md:text-base">{v}</p>
                    <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.16em] text-cream/50 md:text-[10px]">{l}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.55}>
              <div data-testid="founding10-hero-badge" className="mt-5 inline-block rounded-2xl bg-orange-rific px-6 py-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/85">Founding 10</p>
                <p className="mt-1 font-display text-4xl font-extrabold leading-none text-white">
                  ₹0 <span className="text-base font-bold">FRANCHISE FEE</span>
                </p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/85">
                  For the first 10 approved partners
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.65} className="mt-8">
              <ApplyCta placement="hero" />
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.2}>
              <div className="overflow-hidden rounded-[28px] bg-cream">
                <img
                  src={IMG.storefront}
                  alt="RIFIC 15 ft storefront — bold orange exterior, deep green branding, warm cream interior"
                  fetchPriority="high"
                  className="h-auto w-full object-contain"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 01 THE OPPORTUNITY */}
      <Section no="01 — The Opportunity" title="AN INGREDIENT-LED ICE CREAM BRAND.">
        <div className="mt-6 max-w-3xl space-y-5 text-base leading-relaxed text-deep/75 md:text-lg">
          <p>
            India loves ice cream. Very few brands make people stop, look and photograph the store. RIFIC was built
            to be that brand — ingredient-led, visually distinctive and designed for scalable retail.
          </p>
          <p>
            If you have been looking for a food business with genuine shelf appeal, this is the conversation to have.
          </p>
        </div>
      </Section>

      {/* 02 RIFIC PARLOUR MODEL — INVESTMENT */}
      <div className="bg-cream">
        <Section no="02 — RIFIC Parlour Model" title="A SIMPLE INVESTMENT. A DISTINCTIVE BRAND.">
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-deep/75 md:text-lg">
            A compact 200–300 sq. ft. parlour format designed to deliver the complete RIFIC experience with a focused
            investment structure.
          </p>

          {/* Desktop table */}
          <div className="mt-8 hidden overflow-hidden rounded-2xl border border-cream-line bg-white md:block" data-testid="investment-table">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-cream-line bg-deep text-cream">
                  <th className="px-6 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.18em]">Particulars</th>
                  <th className="px-6 py-4 text-right font-mono text-[11px] font-medium uppercase tracking-[0.18em]">Indicative Amount</th>
                </tr>
              </thead>
              <tbody>
                {INVESTMENT_ITEMS.map(([item, amount, desc]) => (
                  <tr key={item} className="border-b border-cream-line/60">
                    <td className="px-6 py-4">
                      <p className="text-sm font-bold text-deep md:text-base">{item}</p>
                      <p className="mt-1 text-xs leading-relaxed text-deep/55">{desc}</p>
                    </td>
                    <td className="px-6 py-4 text-right font-display text-lg font-bold text-deep">{amount}</td>
                  </tr>
                ))}
                <tr className="bg-fresh/10">
                  <td className="px-6 py-4 text-sm font-extrabold uppercase tracking-[0.08em] text-deep">Total Indicative Investment</td>
                  <td className="px-6 py-4 text-right font-display text-xl font-extrabold text-deep" data-testid="investment-total">₹7,75,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile stacked cards */}
          <div className="mt-8 space-y-3 md:hidden" data-testid="investment-cards">
            {INVESTMENT_ITEMS.map(([item, amount, desc]) => (
              <div key={item} className="rounded-2xl border border-cream-line bg-white p-5">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-bold text-deep">{item}</p>
                  <p className="shrink-0 font-display text-lg font-extrabold text-deep" data-testid={`investment-amount-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{amount}</p>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-deep/55">{desc}</p>
              </div>
            ))}
            <div className="rounded-2xl bg-fresh/15 p-5" data-testid="investment-cards-total">
              <div className="flex items-center justify-between">
                <p className="text-sm font-extrabold uppercase tracking-[0.08em] text-deep">Indicative Total</p>
                <p className="font-display text-xl font-extrabold text-deep">₹7,75,000</p>
              </div>
            </div>
          </div>

          <Reveal className="mt-6">
            <div data-testid="planned-investment" className="rounded-2xl bg-orange-rific px-6 py-6 text-center md:px-10 md:py-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/85 md:text-xs">Planned Investment</p>
              <p className="mt-2 font-display text-3xl font-extrabold tracking-[-0.01em] text-white md:text-5xl">UP TO ₹8 LAKH*</p>
            </div>
          </Reveal>

          <p className="mt-6 text-xs leading-relaxed text-deep/50" data-testid="investment-disclaimer">{DISCLAIMER}</p>
        </Section>
      </div>

      {/* 03 FOUNDING 10 CALCULATION */}
      <div className="bg-white">
        <Section no="03 — Founding 10" title="THE ₹1 LAKH DIFFERENCE.">
          <div className="mt-8 grid gap-5 md:grid-cols-2 md:gap-6" data-testid="founding10-comparison">
            {COMPARE.map((c) => (
              <div key={c.title} data-testid={c.tid} className={`overflow-hidden rounded-[28px] border ${c.dark ? "border-deep bg-deep text-cream" : "border-cream-line bg-cream text-deep"}`}>
                <div className="p-6 md:p-8">
                  <h3 className="font-display text-xl font-extrabold uppercase tracking-[-0.01em] md:text-2xl">{c.title}</h3>
                  <div className="mt-5 space-y-3">
                    {c.rows.map(([k, v]) => (
                      <div key={k} className={`flex items-center justify-between border-b pb-3 ${c.dark ? "border-deep-line" : "border-cream-line"}`}>
                        <span className="text-sm font-medium opacity-80 md:text-base">{k}</span>
                        <span className="font-display text-base font-bold md:text-lg">{v}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex items-center justify-between">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-60">Indicative Total</p>
                    <p className={`font-display text-2xl font-extrabold md:text-3xl ${c.dark ? "text-orange-rific" : "text-deep"}`}>{c.total}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center font-display text-xl font-bold tracking-[-0.01em] text-deep md:text-2xl" data-testid="founding10-savings">
            YOU SAVE THE <span className="text-orange-rific">₹1 LAKH</span> STANDARD FRANCHISE FEE.
          </p>
        </Section>
      </div>

      {/* 04 FOUNDING 10 PROGRAMME */}
      <Section no="04 — Limited Launch Programme" title="BECOME ONE OF THE FOUNDING 10." dark>
        <div className="mt-8 grid items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <p className="font-display text-6xl font-extrabold leading-none text-orange-rific md:text-8xl" data-testid="founding10-big-fee">₹0</p>
            <p className="mt-2 font-display text-xl font-bold uppercase tracking-[-0.01em] text-cream md:text-2xl">Franchise Fee</p>
            <p className="mt-5 max-w-md text-base leading-relaxed text-cream/80 md:text-lg">
              We're selecting our first 10 approved franchise partners to help build the next chapter of RIFIC. For
              these Founding 10 partners, the standard ₹1 lakh franchise fee will be completely waived.
            </p>
            <div className="mt-8">
              <ApplyCta placement="founding_section" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl bg-deep-line lg:col-span-6" data-testid="founding10-feature-stats">
            {[
              ["200–300 SQ. FT.", "Parlour Format"],
              ["UP TO ₹8 LAKH*", "Planned Investment"],
              ["₹0", "Training Fee"],
              ["₹0", "Franchise Fee for Founding 10"],
            ].map(([v, l]) => (
              <div key={l} className="bg-deep p-6 md:p-8">
                <p className="font-display text-xl font-extrabold text-cream md:text-2xl">{v}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/55">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* 05 THE BRAND */}
      <div className="bg-cream">
        <Section no="05 — The Brand" title="REAL TASTES BETTER.">
          <div className="mt-6 grid items-center gap-10 lg:grid-cols-2">
            <div className="space-y-5 text-base leading-relaxed text-deep/75 md:text-lg">
              <p>
                Fresh. Bold. Premium. Modern. RIFIC is Indian-born with international design standards — a brand that
                looks as good as it tastes.
              </p>
              <p>
                Our signature orange storefronts, rounded architectural details and the RIPE NOW seasonal platform make
                every store a landmark in its neighbourhood.
              </p>
            </div>
            <div className="overflow-hidden rounded-[28px] bg-cream">
              <img src={IMG.storefrontPortrait} alt="RIFIC brand storefront" loading="lazy" className="h-auto w-full object-contain" />
            </div>
          </div>
        </Section>
      </div>

      {/* 06 PRODUCT UNIVERSE */}
      <div className="bg-white">
        <Section no="06 — Product Universe" title="SCOOPS TO COOLERS.">
          <div className="mt-8 grid items-center gap-10 lg:grid-cols-12" data-testid="franchise-product-universe">
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-[28px] bg-cream">
                <img
                  src={IMG.productUniverse}
                  srcSet={`${IMG.productUniverse} 1672w, ${IMG.productUniverseM} 900w`}
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  alt="RIFIC Alphonso mango ice cream — the product universe"
                  loading="lazy"
                  className="h-auto w-full object-contain"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              {["Ice Creams", "Sundaes", "Waffles", "Milkshakes", "Thickshakes", "Lassi", "Coolers"].map((c, i) => (
                <div key={c} className="flex items-center justify-between border-t border-cream-line py-4 last:border-b md:py-5">
                  <span className="flex items-baseline gap-5">
                    <span className="font-mono text-xs text-orange-rific">0{i + 1}</span>
                    <h3 className="font-display text-xl font-bold uppercase tracking-[-0.01em] text-deep md:text-2xl">{c}</h3>
                  </span>
                  <Link to={`/menu#cat-${c.toLowerCase().replace(/\s+/g, "-")}`} data-testid={`franchise-universe-${c.toLowerCase().replace(/\s+/g, "-")}`} className="text-xs font-bold uppercase tracking-[0.14em] text-deep/50 transition-colors hover:text-orange-rific">
                    View menu
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </div>

      {/* 07 STORE EXPERIENCE */}
      <div className="bg-cream">
        <Section no="07 — Store Experience" title="BOLD OUTSIDE. WARM INSIDE.">
          <div className="mt-8 grid gap-5 md:grid-cols-2 md:gap-6">
            <div className="overflow-hidden rounded-[28px] bg-cream">
              <img src={IMG.storefrontPortrait} alt="RIFIC compact storefront — rounded geometry, premium lighting" loading="lazy" className="h-auto w-full object-contain" />
            </div>
            <div className="overflow-hidden rounded-[28px] bg-cream">
              <img src={IMG.storefront} alt="RIFIC 15 ft storefront at street level" loading="lazy" className="h-auto w-full object-contain" />
            </div>
          </div>
        </Section>
      </div>

      {/* 08 SUPPORT */}
      <Section no="08 — Support" title="YOU'RE NEVER ON YOUR OWN." dark>
        <div className="mt-8 grid gap-px overflow-hidden rounded-2xl bg-deep-line sm:grid-cols-2" data-testid="franchise-support-grid">
          {[
            ["Site & Store Development", "Support in identifying and evaluating the location and implementing the RIFIC store concept."],
            ["Product & Menu Training", "Hands-on product, service and menu training."],
            ["Supply Chain & Operations", "Support for approved products, ingredients, operating processes and day-to-day store standards."],
            ["Brand & Launch Marketing", "Launch guidance, approved brand assets and ongoing central marketing support."],
          ].map(([t, c]) => (
            <div key={t} className="bg-deep p-6 md:p-8">
              <h3 className="font-display text-lg font-bold uppercase tracking-[-0.01em] text-cream md:text-xl">{t}</h3>
              <p className="mt-2 text-base leading-relaxed text-cream/70">{c}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 09 WHO WE'RE LOOKING FOR */}
      <div className="bg-cream">
        <Section no="09 — Who We're Looking For" title="IS THIS YOU?">
          <ul className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="franchise-who-list">
            {[
              "Operators with local market knowledge",
              "People who care deeply about product quality",
              "Retail or F&B experience welcome — not mandatory",
              "Long-term ambition to grow with the brand",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 rounded-2xl border border-cream-line bg-white p-5 text-base font-medium text-deep">
                <span className="mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-orange-rific" />
                {item}
              </li>
            ))}
          </ul>
        </Section>
      </div>

      {/* 10 FRANCHISE JOURNEY */}
      <Section no="10 — Franchise Journey" title="YOUR JOURNEY TO RIFIC.">
        <div className="mt-8 grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="franchise-journey">
          {JOURNEY.map((step, i) => (
            <div key={step} data-testid={`journey-step-${i + 1}`} className="relative border-l-2 border-orange-rific/30 pl-5">
              <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-orange-rific" />
              <p className="font-mono text-xs text-orange-rific">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="mt-2 font-display text-lg font-bold uppercase tracking-[-0.01em] text-deep md:text-xl">{step}</h3>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-deep/55" data-testid="journey-approval-note">
          Founding 10 status is granted only after formal approval by RIFIC.
        </p>
      </Section>

      <Marquee
        className="bg-orange-rific py-4 text-deep md:py-5"
        textClassName="font-display text-xl md:text-2xl font-bold uppercase tracking-[-0.01em]"
        items={["WE PICK IT RIPE.", "WE MAKE IT RIFIC.", "WE PICK IT RIPE.", "WE MAKE IT RIFIC."]}
      />

      {/* 11 FAQ */}
      <Section no="11 — FAQ" title="GOOD QUESTIONS.">
        <div className="mt-8 max-w-3xl" data-testid="franchise-faq">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-cream-line">
                <AccordionTrigger data-testid={`franchise-faq-question-${i + 1}`} className="py-5 text-left font-display text-lg font-bold tracking-[-0.01em] text-deep hover:text-orange-rific md:text-xl [&>svg]:text-orange-rific">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-deep/70">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* 12 FRANCHISE APPLICATION */}
      <section id="franchise-form" className="scroll-mt-20 bg-deep py-16 md:py-24" data-testid="franchise-form-section">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-orange-rific md:text-xs">12 — Franchise Application</p>
            <h2 className="mt-3 font-display text-[clamp(2rem,4.2vw,3.25rem)] font-extrabold tracking-[-0.01em] text-cream">
              APPLY FOR THE FOUNDING 10.
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-cream/75 md:text-lg">
              Tell us about your city, proposed location and plans. Our franchise team will review your application
              and contact suitable applicants.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10 md:mt-14">
            <LeadForm />
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-10 md:py-14">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <p className="max-w-4xl text-xs leading-relaxed text-deep/50" data-testid="franchise-page-disclaimer">{DISCLAIMER}</p>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-cream-line pt-8">
            <p className="font-display text-xl font-bold tracking-[-0.01em] text-deep md:text-2xl">
              Prefer email? Write to us at{" "}
              <a href="mailto:rific.india@gmail.com" data-testid="franchise-email-link" onClick={() => track("email_click", { placement: "franchise_page", mailbox: "rific-india" })} className="text-orange-rific underline-offset-4 hover:underline">
                rific.india@gmail.com
              </a>
            </p>
            <a
              href={`https://wa.me/917249288910?text=${encodeURIComponent("Hi RIFIC Team,\nI'm interested in the RIFIC franchise opportunity.\nI would like to know more about the Founding 10 programme.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="franchise-whatsapp-cta"
              onClick={() => track("whatsapp_click", { placement: "franchise_page" })}
              className="inline-flex items-center gap-2 rounded-full bg-orange-rific px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-deep"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp the Franchise Team
            </a>
            <Link
              to="/stores"
              data-testid="franchise-stores-link"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-deep/60 transition-colors hover:text-orange-rific"
            >
              Or visit a RIFIC store first
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const JOURNEY = [
  "Apply",
  "Discovery Call",
  "Location Evaluation",
  "Commercial Discussion",
  "Approval",
  "Franchise Agreement",
  "Store Development & Training",
  "Launch",
];
